import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import axios from 'axios'
import { IconSearch, IconBell, IconSettings, IconClose } from './icons'

const SEARCH_TABLES = ['Customer', 'Job Site', 'Contacts', 'Estimates', 'Invoices', 'Workorders']

const getInitials = (user) => {
  if (!user?.name) return 'DFL'
  const parts = user.name.split(' ').filter(Boolean)
  if (parts.length > 1) return parts.map(p => p.charAt(0)).join('').slice(0, 3).toUpperCase()
  return user.name.charAt(0).toUpperCase()
}

const getEmployeePhoto = (employee) => {
  if (!employee?.profile_photo_url) return ''
  return employee.profile_photo_url.includes('https')
    ? employee.profile_photo_url
    : `https://${employee.profile_photo_url}`
}

const getResultLabel = (item, table) => {
  if (table === 'Contacts') return [item.firstName, item.lastName].filter(Boolean).join(' ') || item.email || 'Contact'
  if (table === 'Job Site') return item.name || item.address || item.communityName || 'Job site'
  if (table === 'Invoices') return item.invoiceNumber ? `Invoice ${item.invoiceNumber}` : item.jobName || item.name || 'Invoice'
  if (table === 'Workorders') return item.jobNumber ? `Workorder ${item.jobNumber}` : item.jobName || item.name || 'Workorder'
  if (table === 'Estimates') return item.jobNumber ? `Estimate ${item.jobNumber}` : item.jobName || item.name || 'Estimate'
  return item.displayName || item.searchName || item.name || 'Customer'
}

const getResultMeta = (item, table) => {
  const client = item?.client?._id ? item.client : item
  const parts = []
  if (client?.displayName && table !== 'Customer') parts.push(client.displayName)
  if (item.agId) parts.push(`AG ${item.agId}`)
  if (item.email) parts.push(item.email)
  if (item.address) parts.push([item.communityName, item.address, item.buildingNumber ? `Bldg ${item.buildingNumber}` : null].filter(Boolean).join(' '))
  if (item.jobName) parts.push(item.jobName)
  return parts.filter(Boolean).join(' | ')
}

const getResultHref = (item, table) => {
  const client = item?.client?._id ? item.client : item
  if (!client?._id) return null
  if (table === 'Workorders') {
    const jobId = item?.job || item._id
    return `/clients/${client._id}/Job/${jobId}`
  }
  return `/clients/${client._id}/${table}/${item._id || client._id}`
}

function UserMenu({ user, employees }) {
  const history = useHistory()
  const [open, setOpen] = useState(false)
  const menuRef = useRef(null)
  const employee = useMemo(() => {
    if (!user?._id || !employees?.length) return null
    return employees.find(emp => emp.user === user._id || emp.user?._id === user._id) || null
  }, [employees, user])
  const photo = getEmployeePhoto(employee)

  useEffect(() => {
    if (!open) return undefined
    const handleClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  const navigate = (path) => {
    setOpen(false)
    history.push(path)
  }

  return (
    <div className="rail-user-menu" ref={menuRef}>
      <button className="rail-avatar-btn" onClick={() => setOpen(prev => !prev)} title={`${user?.name || 'User'} options`}>
        {photo ? <img src={photo} alt={user?.name || 'User'} /> : <span>{getInitials(user)}</span>}
      </button>
      {open && (
        <div className="rail-user-popover">
          <div className="rail-user-summary">
            <b>{user?.name || 'User'}</b>
            <span>{user?.email || user?.username || 'Portal account'}</span>
          </div>
          <button onClick={() => navigate('/profile')}>Profile</button>
          <button onClick={() => navigate('/myqueue')}>My Queue</button>
          <button onClick={() => navigate('/myestimates')}>My Estimates</button>
          <button onClick={() => navigate('/mypurchaserequests')}>My POs</button>
          <button onClick={() => navigate('/my-lqas')}>My LQAs</button>
          <button onClick={() => navigate('/my-leads')}>My Leads</button>
          <button onClick={() => navigate('/my-route')}>My Route</button>
          <button onClick={() => navigate('/knowledge-base')}>Knowledge Base</button>
          <button className="rail-user-signout" onClick={() => navigate('/logout')}>Sign Out</button>
        </div>
      )}
    </div>
  )
}

function SearchModal({ open, onClose, url }) {
  const [query, setQuery] = useState('')
  const [table, setTable] = useState('Customer')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState([])
  const [searched, setSearched] = useState(false)

  useEffect(() => {
    if (!open) return
    setTimeout(() => {
      const input = document.querySelector('.dashboard-search-input')
      if (input) input.focus()
    }, 0)
  }, [open])

  const runSearch = async (event) => {
    if (event) event.preventDefault()
    if (!query.trim() || !url) return
    setLoading(true)
    setSearched(true)
    try {
      const res = await axios.post(`${url}/api/company/searchEngineRequest`, {
        table,
        value: query.trim(),
        field: null
      })
      setResults(res.data?.docs || [])
    } catch (err) {
      console.error('[DASHBOARD] Search failed:', err)
      setResults([])
    } finally {
      setLoading(false)
    }
  }

  if (!open) return null

  return (
    <div className="dashboard-modal-backdrop" onMouseDown={onClose}>
      <div className="dashboard-modal dashboard-search-modal" onMouseDown={e => e.stopPropagation()}>
        <div className="dashboard-modal-head">
          <div>
            <h3>Search Portal</h3>
            <span>Uses the existing database search engine.</span>
          </div>
          <button className="rail-icon-btn" onClick={onClose} title="Close"><IconClose size={16} /></button>
        </div>
        <form className="dashboard-search-form" onSubmit={runSearch}>
          <div className="dashboard-search-field">
            <IconSearch size={15} />
            <input
              className="dashboard-search-input"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search customers, job sites, contacts, estimates..."
            />
          </div>
          <select value={table} onChange={e => setTable(e.target.value)}>
            {SEARCH_TABLES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <button className="dbtn dbtn-primary" type="submit" disabled={!query.trim() || loading}>
            {loading ? 'Searching...' : 'Search'}
          </button>
        </form>
        <div className="dashboard-search-results">
          {loading && <div className="dashboard-empty-state">Searching...</div>}
          {!loading && searched && results.length === 0 && <div className="dashboard-empty-state">No matches found.</div>}
          {!loading && results.map(item => {
            const href = getResultHref(item, table)
            const content = (
              <>
                <b>{getResultLabel(item, table)}</b>
                <span>{getResultMeta(item, table) || table}</span>
              </>
            )
            return href ? (
              <Link key={item._id || `${table}-${getResultLabel(item, table)}`} to={href} target="_blank" onClick={onClose}>
                {content}
              </Link>
            ) : (
              <div key={item._id || `${table}-${getResultLabel(item, table)}`}>{content}</div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default function TopRail({ onQuickCalc, onQuickQuote, url, user, employees, tasks }) {
  const [searchOpen, setSearchOpen] = useState(false)
  const path = typeof window !== 'undefined' ? window.location.pathname : ''
  const taskCount = Array.isArray(tasks) ? tasks.filter(t => t.status !== 'Complete').length : 0

  return (
    <div className="dashboard-v2-rail">
      <Link className="rail-brand" to="/hmi-v2">
        <span className="glyph">H</span>
        H.M.I Portal
      </Link>
      <nav className="rail-nav">
        <Link className={path === '/hmi-v2' ? 'active' : ''} to="/hmi-v2">Dashboard</Link>
        <Link className={path === '/unclaimed-work' ? 'active' : ''} to="/unclaimed-work">Work Queue</Link>
        <Link to="/tasks">Tasks</Link>
        <Link to="/reports">Reports</Link>
      </nav>
      <div className="rail-quick-actions">
        <button className="dbtn dbtn-outline dbtn-sm" onClick={onQuickCalc}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="2" width="16" height="20" rx="2" /><line x1="8" y1="6" x2="16" y2="6" /><line x1="8" y1="10" x2="10" y2="10" /><line x1="14" y1="10" x2="16" y2="10" /><line x1="8" y1="14" x2="10" y2="14" /><line x1="14" y1="14" x2="16" y2="14" /><line x1="8" y1="18" x2="16" y2="18" />
          </svg>
          Quick Calculator
        </button>
        <button className="dbtn dbtn-outline dbtn-sm" onClick={onQuickQuote}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="12" y1="18" x2="12" y2="12" /><line x1="9" y1="15" x2="15" y2="15" />
          </svg>
          Quick Quote
        </button>
      </div>
      <div className="rail-spacer" />
      <button className="rail-icon-btn" title="Search" onClick={() => setSearchOpen(true)}>
        <IconSearch size={16} />
      </button>
      <Link className={`rail-icon-btn${taskCount > 0 ? ' has-badge' : ''}`} to="/tasks" title="Tasks and Alerts">
        <IconBell size={16} />
        {taskCount > 0 && <span className="rail-count">{taskCount}</span>}
      </Link>
      <Link className="rail-icon-btn" to="/settings" title="Configs / Settings">
        <IconSettings size={16} />
      </Link>
      <UserMenu user={user} employees={employees} />
      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} url={url} />
    </div>
  )
}
